import React, { useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import styles from "./CreatePropertyModal.module.css";
import ReactModal from "react-modal";
import Icon from "../Icon/Icon";

type CreatePropertyModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  existingFieldsNames: string[];
  existingKeywords?: string[];
  onCreate: ({
    label,
    keywords,
    queries,
  }: {
    label: string;
    keywords: { keyword: string }[];
    queries: { question: string; alias?: string }[];
  }) => void;
  getSampleValue?: (keyword: string) => string;
};

const getValidationSchema = (existingFieldsNames: string[]) =>
  Yup.object({
    name: Yup.string()
      .trim()
      .required("El nombre es obligatorio")
      .test(
        "unique-name",
        "El nombre ya existe",
        (value) =>
          !existingFieldsNames.some((existingName) => {
            return (
              existingName.toLowerCase() === (value?.trim().toLowerCase() || "")
            );
          })
      ),
    keywords: Yup.array().of(
      Yup.object({
        keyword: Yup.string().trim().required(),
      })
    ), // No .min(1)
    queries: Yup.array().of(
      Yup.object({
        question: Yup.string()
          .trim()
          .required("La pregunta es obligatoria")
          .max(200, "La pregunta debe tener menos de 200 caracteres")
          .matches(
            /^[a-z0-9\w\s!"#$%'&()*+,\-./\\:;=?@[\]^_`{|}~><]+$/i,
            "Caracteres inválidos en la pregunta"
          ),
        alias: Yup.string().trim().optional(),
      })
    ),
  });

const CreatePropertyModal = ({
  isOpen,
  onRequestClose,
  onCreate,
  existingFieldsNames,
  existingKeywords,
  getSampleValue,
}: CreatePropertyModalProps) => {
  const formik = useFormik({
    initialValues: {
      name: "",
      keywordInput: "",
      keywords: [] as { keyword: string }[],
      queries: [] as { question: string; alias?: string }[],
      queryInput: "",
      aliasInput: "",
    },
    validationSchema: getValidationSchema(existingFieldsNames),
    onSubmit: (values) => {
      let queries = values.queries;
      const trimmedQueryInput = values.queryInput.trim();
      const trimmedAliasInput = values.aliasInput.trim();
      if (trimmedQueryInput) {
        queries = [
          ...queries,
          {
            question: trimmedQueryInput,
            alias: trimmedAliasInput || undefined,
          },
        ];
      }
      onCreate({
        label: values.name.trim(),
        keywords: values.keywords,
        queries,
      });
      formik.resetForm();
      onRequestClose();
    },
  });

  const keywordsInputRef = useRef<HTMLInputElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputTrimmed = formik.values.keywordInput.trim();
  const lowercasedInput = inputTrimmed.toLowerCase();
  const uniqueExistingKeywords = Array.from(new Set(existingKeywords));

  const filteredExistingKeywords = inputTrimmed
    ? uniqueExistingKeywords
        .filter((keyword) => keyword.toLowerCase().includes(lowercasedInput))
        .filter(
          (keyword) =>
            !formik.values.keywords.some(
              (k) => k.keyword.toLowerCase() === keyword.toLowerCase()
            )
        )
    : [];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = formik.values.keywordInput.trim();
      if (
        trimmed &&
        !formik.values.keywords.some(
          (k) => k.keyword.toLowerCase() === trimmed.toLowerCase()
        )
      ) {
        formik.setFieldValue("keywords", [
          ...formik.values.keywords,
          { keyword: trimmed },
        ]);
        formik.setFieldValue("keywordInput", "");
        setShowSuggestions(false);
        keywordsInputRef.current?.blur();
      }
    }
  };

  const deleteKeyword = (keywordToDelete: string) => {
    formik.setFieldValue(
      "keywords",
      formik.values.keywords.filter((k) => k.keyword !== keywordToDelete)
    );
  };

  const handleAddKeywordFromSuggestion = (keyword: string) => {
    const trimmed = keyword.trim();
    if (
      trimmed &&
      !formik.values.keywords.some(
        (k) => k.keyword.toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      formik.setFieldValue("keywords", [
        ...formik.values.keywords,
        { keyword: trimmed },
      ]);
      formik.setFieldValue("keywordInput", "");
      setShowSuggestions(false);
      keywordsInputRef.current?.blur();
    }
  };

  const handleInputFocus = () => setShowSuggestions(true);
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (
        !e.relatedTarget ||
        !e.relatedTarget.closest(`.${styles.searchResultsList}`)
      ) {
        setShowSuggestions(false);
      }
    }, 100);
  };

  // State for editing a query (question)
  const [editingQueryIndex, setEditingQueryIndex] = useState<number | null>(
    null
  );
  const [editingQueryValue, setEditingQueryValue] = useState("");
  const [editingQueryError, setEditingQueryError] = useState<string>("");
  const [queryInputError, setQueryInputError] = useState<string>("");

  // Add handlers for queries
  const handleAddQuery = async () => {
    const question = formik.values.queryInput.trim();
    const alias = formik.values.aliasInput.trim();
    // Validate question using Yup
    try {
      await Yup.string()
        .trim()
        .required("La pregunta es obligatoria")
        .max(200, "La pregunta debe tener menos de 200 caracteres")
        .matches(
          /^[a-z0-9\w\s!"#$%'&()*+,\-./\\:;=?@[\]^_`{|}~><]+$/i,
          "Caracteres inválidos en la pregunta"
        )
        .validate(question);
      setQueryInputError("");
      if (question) {
        formik.setFieldValue("queries", [
          ...formik.values.queries,
          { question, alias: alias || undefined },
        ]);
        formik.setFieldValue("queryInput", "");
        formik.setFieldValue("aliasInput", "");
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setQueryInputError(err.message);
      } else {
        setQueryInputError("Error desconocido");
      }
    }
  };

  // Handler to start editing a query
  const handleEditQuery = (idx: number) => {
    setEditingQueryIndex(idx);
    setEditingQueryValue(formik.values.queries[idx].question);
  };

  // Handler to save the edited query
  const handleSaveEditedQuery = async (idx: number) => {
    const trimmed = editingQueryValue.trim();
    // Validate edited query using Yup
    try {
      await Yup.string()
        .trim()
        .required("La pregunta es obligatoria")
        .max(200, "La pregunta debe tener menos de 200 caracteres")
        .matches(
          /^[a-z0-9\w\s!"#$%'&()*+,\-./\\:;=?@[\]^_`{|}~><]+$/i,
          "Caracteres inválidos en la pregunta"
        )
        .validate(trimmed);
      setEditingQueryError("");
      if (trimmed) {
        const updatedQueries = formik.values.queries.map((q, i) =>
          i === idx ? { ...q, question: trimmed } : q
        );
        formik.setFieldValue("queries", updatedQueries);
      }
      setEditingQueryIndex(null);
      setEditingQueryValue("");
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setEditingQueryError(err.message);
      } else {
        setEditingQueryError("Error desconocido");
      }
    }
  };

  // Handler to cancel editing
  const handleCancelEditQuery = () => {
    setEditingQueryIndex(null);
    setEditingQueryValue("");
  };

  const deleteQuery = (idx: number) => {
    formik.setFieldValue(
      "queries",
      formik.values.queries.filter((_, i) => i !== idx)
    );
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
      ariaHideApp={false}
    >
      <button className={styles.closeIcon} onClick={onRequestClose}>
        <Icon name="x-mark" size={16} />
      </button>
      <h2 className={styles.title}>
        Crear Propiedad <Icon name="edit-pen" size={32} />
      </h2>
      <p className={styles.subtitle}>
        Debes crear una propiedad para poder extraer la información del
        documento
      </p>

      <form onSubmit={formik.handleSubmit} className={styles.form}>
        <label className={styles.label}>
          Nombre de la propiedad
          {formik.touched.name && formik.errors.name && (
            <div className={styles.textWarning}>{formik.errors.name}</div>
          )}
          <input
            type="text"
            name="name"
            placeholder="Ej: Numero de caso"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={styles.nameInput}
          />
        </label>

        <div className={styles.label}>
          Palabras clave
          {formik.values.keywords.length > 0 && (
            <ul className={styles.keywordsList}>
              {formik.values.keywords.map((k, i) => (
                <li key={i} className={styles.keywordsListItem}>
                  {k.keyword}
                  <button
                    type="button"
                    className={styles.deleteKeywordIcon}
                    onClick={() => deleteKeyword(k.keyword)}
                  >
                    <Icon name="x-mark" size={20} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {formik.touched.keywords && formik.errors.keywords && (
            <div className={styles.textWarning}>
              {typeof formik.errors.keywords === "string"
                ? formik.errors.keywords
                : null}
            </div>
          )}
          <div className={styles.keywordsInputContainer}>
            <input
              type="text"
              name="keywordInput"
              placeholder="Palabras clave aquí"
              value={formik.values.keywordInput}
              onChange={formik.handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              className={`${styles.keywordsInput} ${
                showSuggestions && filteredExistingKeywords.length > 0
                  ? styles.keywordsInputActive
                  : ""
              }`}
              autoComplete="off"
              ref={keywordsInputRef}
            />
            {showSuggestions && filteredExistingKeywords.length > 0 && (
              <ul className={styles.searchResultsList}>
                {filteredExistingKeywords.map((keyword, index) => {
                  const sampleValue =
                    typeof getSampleValue === "function"
                      ? getSampleValue(keyword)
                      : "";
                  return (
                    <li
                      key={index}
                      className={styles.searchResultItem}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleAddKeywordFromSuggestion(keyword)}
                    >
                      {keyword}
                      {sampleValue && ` (${sampleValue})`}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Queries/questions for AWS Textract */}
        <div className={styles.label}>
          Preguntas para AWS Textract (opcional)
          {formik.values.queries.length > 0 && (
            <ul className={styles.keywordsList}>
              {formik.values.queries.map((q, i) => (
                <li key={i} className={styles.keywordsListItem}>
                  {editingQueryIndex === i ? (
                    // If editing, show input field
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <input
                        type="text"
                        value={editingQueryValue}
                        autoFocus
                        onChange={(e) => {
                          setEditingQueryValue(e.target.value);
                          setEditingQueryError("");
                        }}
                        onBlur={() => handleSaveEditedQuery(i)}
                        onKeyDown={async (e) => {
                          if (e.key === "Enter") await handleSaveEditedQuery(i);
                          if (e.key === "Escape") handleCancelEditQuery();
                        }}
                        className={styles.keywordsInput}
                        style={{ marginRight: 8 }}
                      />
                      {editingQueryError && (
                        <div className={styles.textWarning}>
                          {editingQueryError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {q.question}
                      <button
                        type="button"
                        className={styles.deleteKeywordIcon}
                        style={{ marginLeft: 4 }}
                        onClick={() => handleEditQuery(i)}
                        title="Editar pregunta"
                      >
                        <Icon name="pencil" size={16} />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className={styles.deleteKeywordIcon}
                    onClick={() => deleteQuery(i)}
                    title="Eliminar pregunta"
                  >
                    <Icon name="x-mark" size={20} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {formik.touched.queries && formik.errors.queries && (
            <div className={styles.textWarning}>
              {typeof formik.errors.queries === "string"
                ? formik.errors.queries
                : null}
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 8,
              flexDirection: "column",
            }}
          >
            <input
              type="text"
              name="queryInput"
              placeholder="Pregunta para Textract"
              value={formik.values.queryInput}
              onChange={formik.handleChange}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  await handleAddQuery();
                }
              }}
              style={{ flex: 2 }}
              className={styles.keywordsInput}
              autoComplete="off"
            />
            {queryInputError && (
              <div className={styles.textWarning}>{queryInputError}</div>
            )}
          </div>
        </div>

        <button type="submit" className={styles.createButton}>
          Crear <Icon name="stars" size={20} />
        </button>
      </form>
    </ReactModal>
  );
};

export default CreatePropertyModal;
